import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { DEFAULT_DOCK_MODE } from "../config";
import { LAYOUT_CONFIG } from "../Layout/defaults";
import type { ConversationPanelState, DockMode, ShellMode } from "../types";
import { STORE_KEY, isDockMode } from "../utils";
import {
  type ConversationPanelSurfaceSlice,
  createConversationPanelSurfaceSlice,
} from "./conversationPanelSurfaceSlice";
import { dedupedDockStorage } from "./dockStorage";

export type CircleAIShellStateKeys =
  | "mode"
  | "userMode"
  | "dockMode"
  | "isDockManualOverride"
  | "isAdminDockForced"
  | "conversationPanelState"
  | "conversationPanelWidth"
  | "isLeftSidebarVisible"
  | "isRightSidebarVisible"
  | "leftSidebarWidth"
  | "leftSidebarMinWidth"
  | "rightSidebarWidth"
  | "dismissedBanners";

// `mode`: live session mode, re-seeded from role on each mount.
// `userMode`: the user's explicit choice (null until chosen), set via
// `setUserMode` so transient `setMode` calls (e.g. /ai page) can't clobber it.
// Only `dockMode` is persisted (admin sidebar expand/collapse preference).
// Transient dock overrides (such as the promotion-locked layout) are not
// written to storage.
interface ModeSlice {
  mode: ShellMode;
  setMode: (mode: ShellMode) => void;
  setUserMode: (mode: ShellMode) => void;
  userMode: ShellMode | null;
}

interface DockSlice {
  _baseDockMode: DockMode | null;
  _dockOverrides: Array<{ id: string; mode: DockMode }>;
  dockMode: DockMode;
  isAdminDockForced: boolean;
  // Session-scoped guard so onboarding cannot overwrite an explicit toggle.
  isDockManualOverride: boolean;
  popDockOverride: (id: string) => void;
  pushDockOverride: (id: string, mode: DockMode) => void;
  setAdminDockForced: (forced: boolean) => void;
  setDockManualOverride: (override: boolean) => void;
  setDockMode: (mode: DockMode) => void;
  toggleDockMode: () => void;
}

interface ConversationPanelSlice {
  conversationPanelState: ConversationPanelState;
  conversationPanelWidth: number;
  setConversationPanelState: (state: ConversationPanelState) => void;
  setConversationPanelWidth: (width: number) => void;
}

interface CommunitySidebarsSlice {
  isLeftSidebarVisible: boolean;
  isRightSidebarVisible: boolean;
  leftSidebarMinWidth: number;
  leftSidebarWidth: number;
  rightSidebarWidth: number;
  setLeftSidebarMinWidth: (minWidth: number) => void;
  setLeftSidebarVisible: (visible: boolean) => void;
  setLeftSidebarWidth: (width: number) => void;
  setRightSidebarVisible: (visible: boolean) => void;
  setRightSidebarWidth: (width: number) => void;
  toggleLeftSidebarVisible: () => void;
  toggleRightSidebarVisible: () => void;
}

interface BannersSlice {
  dismissBanner: (id: string) => void;
  dismissedBanners: Record<string, true>;
  restoreAllBanners: () => void;
  restoreBanner: (id: string) => void;
}

export interface CircleAIShellStore
  extends
    ModeSlice,
    DockSlice,
    ConversationPanelSlice,
    ConversationPanelSurfaceSlice,
    CommunitySidebarsSlice,
    BannersSlice {}

export type CircleAIShellStateProps = Pick<
  CircleAIShellStore,
  CircleAIShellStateKeys
>;

export const DEFAULT_STATE: CircleAIShellStateProps = {
  mode: "community",
  userMode: null,
  dockMode: DEFAULT_DOCK_MODE,
  isAdminDockForced: false,
  isDockManualOverride: false,
  conversationPanelState: "closed",
  conversationPanelWidth: LAYOUT_CONFIG.conversationPanel.default,
  isLeftSidebarVisible: true,
  isRightSidebarVisible: false,
  leftSidebarWidth: LAYOUT_CONFIG.communityLeftSidebar.default,
  leftSidebarMinWidth: LAYOUT_CONFIG.communityLeftSidebar.minWidth,
  rightSidebarWidth: LAYOUT_CONFIG.communityRightSidebar.default,
  dismissedBanners: {},
};

type PersistedShellState = Pick<CircleAIShellStore, "dockMode">;

const persistedDockMode = ({
  dockMode,
  _baseDockMode,
  _dockOverrides,
}: Pick<
  CircleAIShellStore,
  "dockMode" | "_baseDockMode" | "_dockOverrides"
>): DockMode =>
  _dockOverrides.length > 0 ? (_baseDockMode ?? dockMode) : dockMode;

export const useCircleAIShellStore = create<CircleAIShellStore>()(
  persist(
    set => ({
      ...DEFAULT_STATE,
      _dockOverrides: [],
      _baseDockMode: null,
      ...createConversationPanelSurfaceSlice(set),
      setMode: mode => set({ mode }),
      setUserMode: mode => set({ mode, userMode: mode }),

      // Sets the base dock mode. While transient overrides are active the
      // override (top of stack) keeps winning visually, so the explicit intent
      // is recorded as the base that pops restore — otherwise popDockOverride
      // would silently discard it.
      setDockMode: dockMode =>
        set(s =>
          s._dockOverrides.length > 0
            ? { _baseDockMode: dockMode }
            : { dockMode },
        ),
      // Same overrides-active guard as setDockMode: while an override is on
      // top, the user's toggle flips the base that pops restore, so it isn't
      // discarded when the override ends.
      toggleDockMode: () =>
        set(s => {
          const next: DockMode =
            s.dockMode === "expanded" ? "collapsed" : "expanded";
          return s._dockOverrides.length > 0
            ? { _baseDockMode: next, isDockManualOverride: true }
            : { dockMode: next, isDockManualOverride: true };
        }),
      setDockManualOverride: isDockManualOverride =>
        set({ isDockManualOverride }),
      setAdminDockForced: isAdminDockForced => set({ isAdminDockForced }),
      pushDockOverride: (id, mode) =>
        set(s => {
          // Capture the pre-override dock mode as the base to restore. A
          // transient collapse must not become the user's preference when the
          // override ends.
          const baseDockMode =
            s._dockOverrides.length === 0 ? s.dockMode : s._baseDockMode;
          const overrides = s._dockOverrides.filter(o => o.id !== id);
          overrides.push({ id, mode });
          return {
            _dockOverrides: overrides,
            _baseDockMode: baseDockMode,
            dockMode: mode,
          };
        }),
      popDockOverride: id =>
        set(s => {
          const overrides = s._dockOverrides.filter(o => o.id !== id);
          const last = overrides.at(-1);
          if (last) {
            return { _dockOverrides: overrides, dockMode: last.mode };
          }
          return {
            _dockOverrides: [],
            _baseDockMode: null,
            dockMode: s._baseDockMode ?? s.dockMode,
          };
        }),
      setConversationPanelState: conversationPanelState =>
        set(s =>
          conversationPanelState === "closed" &&
          s.activeConversationPanelSurface !== null
            ? { conversationPanelState, activeConversationPanelSurface: null }
            : { conversationPanelState },
        ),

      setLeftSidebarVisible: v =>
        set(s =>
          s.isLeftSidebarVisible === v ? s : { isLeftSidebarVisible: v },
        ),
      setRightSidebarVisible: v =>
        set(s =>
          s.isRightSidebarVisible === v ? s : { isRightSidebarVisible: v },
        ),
      toggleLeftSidebarVisible: () =>
        set(s => ({ isLeftSidebarVisible: !s.isLeftSidebarVisible })),
      toggleRightSidebarVisible: () =>
        set(s => ({ isRightSidebarVisible: !s.isRightSidebarVisible })),
      setConversationPanelWidth: w =>
        set(s =>
          s.conversationPanelWidth === w ? s : { conversationPanelWidth: w },
        ),
      setLeftSidebarWidth: w =>
        set(s => (s.leftSidebarWidth === w ? s : { leftSidebarWidth: w })),
      setLeftSidebarMinWidth: w =>
        set(s =>
          s.leftSidebarMinWidth === w ? s : { leftSidebarMinWidth: w },
        ),
      setRightSidebarWidth: w =>
        set(s => (s.rightSidebarWidth === w ? s : { rightSidebarWidth: w })),

      dismissBanner: id =>
        set(s => ({ dismissedBanners: { ...s.dismissedBanners, [id]: true } })),
      restoreBanner: id =>
        set(s => {
          const next = { ...s.dismissedBanners };
          delete next[id];
          return { dismissedBanners: next };
        }),
      restoreAllBanners: () => set({ dismissedBanners: {} }),
    }),
    {
      name: STORE_KEY,
      storage: createJSONStorage(() => dedupedDockStorage),
      version: 2,
      partialize: (state): PersistedShellState => ({
        dockMode: persistedDockMode(state),
      }),
      merge: (persisted, current) => {
        if (
          persisted &&
          typeof persisted === "object" &&
          "dockMode" in persisted &&
          isDockMode(persisted.dockMode)
        ) {
          return { ...current, dockMode: persisted.dockMode };
        }
        return current;
      },
      migrate: (persistedState): PersistedShellState => {
        if (
          persistedState &&
          typeof persistedState === "object" &&
          "dockMode" in persistedState &&
          isDockMode(persistedState.dockMode)
        ) {
          return { dockMode: persistedState.dockMode };
        }
        return { dockMode: DEFAULT_DOCK_MODE };
      },
    },
  ),
);
